import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, readErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { gtmPush } from '@/lib/gtm';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/toast';
import { fetchRegion, formatPrice, loadPaypalSdk } from '@/lib/region';
import { ArrowLeft, Star, Clock, Check, Loader2 } from 'lucide-react';
import ProfilePicker from '@/components/ProfilePicker';

/**
 * Round 40l — currency-aware expert booking.
 *
 * INR users: existing Razorpay flow (unchanged, 7 new tiers).
 * USD users: PayPal Smart Buttons in a popup container; on capture,
 * the consultation flips from pending_payment → pending_schedule and
 * the user is navigated to /app/profile (same as the INR happy path).
 */
export default function ExpertDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [expert, setExpert] = useState(null);
    const [tier, setTier] = useState(null);
    const [busy, setBusy] = useState(false);
    const [profileId, setProfileId] = useState(null);
    const [currency, setCurrency] = useState('INR');
    // PayPal popup container — mounted on demand inside a centred sheet.
    const [paypalOpen, setPaypalOpen] = useState(false);
    const [pendingConsultation, setPendingConsultation] = useState(null);
    const paypalContainerRef = useRef(null);

    useEffect(() => {
        api.get(`/consultations/experts/${id}`).then((r) => {
            setExpert(r.data);
            // Default selection: 30-min tier (mid of the 7-tier matrix).
            const def = r.data.tiers?.find((t) => t.duration_min === 30) || r.data.tiers?.[0];
            setTier(def);
        }).catch(() => toast.error('Expert not found'));
        fetchRegion()
            .then((r) => setCurrency(r?.currency || 'INR'))
            .catch(() => { /* default INR */ });
    }, [id]);

    const isUsd = currency === 'USD';
    const tierAmount = tier ? (isUsd ? Number(tier.amount_usd || 0) : Number(tier.amount_inr || 0)) : 0;
    const tierLabel = tier ? formatPrice(tierAmount, currency) : '';

    const bookAndPay = async () => {
        if (!tier || !expert) return;
        setBusy(true);
        try {
            const { data: c } = await api.post('/consultations/book', {
                expert_id: expert.id,
                tier: String(tier.duration_min),
                profile_id: profileId,
            });
            const { data: pay } = await api.post(`/consultations/${c.id}/pay`, {
                origin_url: window.location.origin,
            });
            if (pay.provider === 'paypal') {
                // USD path — open Smart Buttons popup.
                setPendingConsultation({ ...c, order_id: pay.order_id, amount_usd: pay.amount_usd });
                setPaypalOpen(true);
                // busy stays true while the popup is open; cleared in the buttons callback.
            } else {
                await openRazorpay(c, pay);
            }
        } catch (err) {
            toast.error(readErr(err, 'Booking failed'));
            setBusy(false);
        }
    };

    const openRazorpay = async (c, pay) => {
        await loadRazorpayScript();
        const rz = new window.Razorpay({
            key: pay.key_id,
            order_id: pay.order_id,
            amount: pay.amount,
            currency: pay.currency,
            name: 'AstroConsult',
            description: `${expert.name} · ${tier.duration_label || tier.duration_min + ' min'}`,
            theme: { color: '#F59E0B' },
            config: {
                display: {
                    blocks: {
                        upi_block: {
                            name: 'Pay using UPI',
                            instruments: [{ method: 'upi', flows: ['collect', 'intent', 'qr'] }],
                        },
                        other_block: {
                            name: 'Other methods',
                            instruments: [{ method: 'card' }, { method: 'netbanking' }, { method: 'wallet' }],
                        },
                    },
                    sequence: ['block.upi_block', 'block.other_block'],
                    preferences: { show_default_blocks: false },
                },
            },
            modal: {
                ondismiss: () => {
                    toast.message('Payment cancelled');
                    setBusy(false);
                },
            },
            handler: async (res) => {
                try {
                    await api.post('/payments/razorpay/verify', {
                        razorpay_order_id: res.razorpay_order_id,
                        razorpay_payment_id: res.razorpay_payment_id,
                        razorpay_signature: res.razorpay_signature,
                    });
                    gtmPush({
                        event: 'purchase',
                        transaction_id: res.razorpay_payment_id,
                        value: Number(pay.amount) / 100,
                        currency: 'INR',
                        user_id: user?.id,
                    });
                    toast.success('Booking confirmed');
                    navigate('/app/profile');
                } catch (err) {
                    toast.error(readErr(err, 'Payment verification failed'));
                } finally {
                    setBusy(false);
                }
            },
        });
        rz.on('payment.failed', (resp) => {
            toast.error(resp?.error?.description || 'Payment failed — please try again.');
            setBusy(false);
        });
        rz.open();
    };

    // Mount PayPal Smart Buttons whenever the popup opens.
    useEffect(() => {
        if (!paypalOpen || !pendingConsultation) return;
        let cancelled = false;
        (async () => {
            try {
                const cfg = await api.get('/payments/paypal/config').then((r) => r.data);
                const paypal = await loadPaypalSdk({ clientId: cfg.client_id, currency: 'USD' });
                if (cancelled || !paypal || !paypalContainerRef.current) return;
                paypalContainerRef.current.innerHTML = '';
                paypal.Buttons({
                    style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' },
                    createOrder: () => pendingConsultation.order_id,
                    onApprove: async () => {
                        try {
                            await api.post(`/consultations/${pendingConsultation.id}/paypal-capture`, {
                                order_id: pendingConsultation.order_id,
                            });
                            gtmPush({
                                event: 'purchase',
                                transaction_id: pendingConsultation.order_id,
                                value: pendingConsultation.amount_usd,
                                currency: 'USD',
                                user_id: user?.id,
                            });
                            toast.success('Booking confirmed');
                            setPaypalOpen(false);
                            setBusy(false);
                            navigate('/app/profile');
                        } catch (err) {
                            toast.error(readErr(err, 'Payment capture failed'));
                            setBusy(false);
                        }
                    },
                    onCancel: () => {
                        toast.message('Payment cancelled');
                        setPaypalOpen(false);
                        setBusy(false);
                    },
                    onError: (err) => {
                        toast.error(err?.message || 'PayPal error — please try again.');
                        setPaypalOpen(false);
                        setBusy(false);
                    },
                }).render(paypalContainerRef.current);
            } catch (err) {
                toast.error('Could not load PayPal — please try again.');
                setPaypalOpen(false);
                setBusy(false);
            }
        })();
        return () => { cancelled = true; };
    }, [paypalOpen, pendingConsultation, user?.id, navigate]);

    if (!expert) return <div className="max-w-4xl mx-auto px-6 py-16 text-slate-500">Loading expert…</div>;

    return (
        <div className="max-w-5xl mx-auto px-6 py-10 md:py-14" data-testid="expert-detail-page">
            <Link to="/app/consult" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-marigold mb-8">
                <ArrowLeft size={14} /> Back to experts
            </Link>

            <div className="grid lg:grid-cols-5 gap-10">
                <div className="lg:col-span-2">
                    <Card className="bg-elevated border-marigold/15 rounded-sm overflow-hidden">
                        <div className="aspect-[4/5]">
                            <img src={expert.image_url} alt={expert.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-3">
                                <Star size={14} className="text-marigold fill-marigold" />
                                <span className="text-parchment font-medium">{expert.rating}</span>
                                <span className="w-1 h-1 rounded-full bg-slate-700" />
                                <Clock size={12} className="text-slate-500" />
                                <span className="text-xs text-slate-500">{expert.years_experience} years</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {(expert.specializations || []).map((s) => (
                                    <span key={s} className="text-[10px] uppercase tracking-widest text-copper bg-marigold/5 border border-marigold/15 px-2 py-1 rounded-sm">{s}</span>
                                ))}
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="lg:col-span-3 space-y-8">
                    <div>
                        <div className="overline mb-3">Consulting astrologer</div>
                        <h1 className="font-serif text-4xl md:text-5xl text-parchment">{expert.name}</h1>
                        <p className="mt-4 text-slate-300 leading-relaxed">{expert.bio}</p>
                    </div>

                    <Card className="bg-surface border-marigold/15 rounded-sm p-6">
                        <div className="flex items-center justify-between mb-5">
                            <div className="overline">Book a session</div>
                        </div>
                        <div className="mb-4" data-testid="expert-profile-section">
                            <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">Reading is for</label>
                            <ProfilePicker
                                value={profileId}
                                onChange={(pid) => setProfileId(pid)}
                                testIdPrefix="expert-profile"
                                compact
                            />
                        </div>
                        <div className="space-y-2">
                            {(expert.tiers || []).map((t) => {
                                const selected = tier?.duration_min === t.duration_min;
                                const amount = isUsd ? Number(t.amount_usd || 0) : Number(t.amount_inr || 0);
                                return (
                                    <button
                                        key={t.duration_min}
                                        onClick={() => setTier(t)}
                                        data-testid={`tier-${t.duration_min}`}
                                        className={`relative w-full flex items-center justify-between p-4 rounded-sm border transition-all ${
                                            selected ? 'bg-marigold/10 border-marigold' : 'bg-obsidian border-marigold/10 hover:border-marigold/40'
                                        }`}
                                    >
                                        {t.duration_min === 30 && (
                                            <span
                                                data-testid="most-popular-ribbon"
                                                className="absolute -top-2.5 left-4 bg-marigold text-obsidian text-[10px] uppercase tracking-widest px-2 py-0.5 font-semibold rounded-sm shadow-[0_4px_16px_rgba(212,168,83,0.25)]"
                                            >
                                                Most popular
                                            </span>
                                        )}
                                        <div className="text-left">
                                            <span className="font-serif text-lg text-parchment">
                                                {t.duration_label || `${t.duration_min} mins`}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-serif text-xl text-parchment" data-testid={`tier-${t.duration_min}-amount`}>
                                                {formatPrice(amount, currency)}
                                            </span>
                                            {selected && <Check className="text-marigold" size={18} />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <Button
                            onClick={bookAndPay}
                            disabled={busy || !tier}
                            data-testid="book-and-pay"
                            className="w-full mt-6 bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11"
                        >
                            {busy
                                ? <><Loader2 className="animate-spin mr-2" size={16} /> Preparing payment…</>
                                : `Book & pay · ${tierLabel}`}
                        </Button>
                        <p className="mt-3 text-xs text-slate-500 text-center">
                            After payment, we'll email to confirm a slot within 24 hours.
                        </p>
                    </Card>
                </div>
            </div>

            {/* PayPal Smart Buttons popup (USD users) */}
            {paypalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
                    data-testid="paypal-popup"
                    role="dialog"
                    aria-modal="true"
                >
                    <Card className="relative bg-elevated border-marigold/30 rounded-sm p-6 max-w-sm w-full">
                        <button
                            type="button"
                            onClick={() => { setPaypalOpen(false); setBusy(false); }}
                            className="absolute top-3 right-3 text-slate-400 hover:text-parchment text-xl leading-none"
                            data-testid="paypal-popup-close"
                            aria-label="Cancel payment"
                        >×</button>
                        <div className="overline mb-2">Complete payment</div>
                        <div className="font-serif text-xl text-parchment mb-1">
                            {expert.name} · {tier?.duration_label}
                        </div>
                        <div className="text-sm text-slate-400 mb-4">{tierLabel}</div>
                        <div ref={paypalContainerRef} data-testid="paypal-buttons-container" />
                        <p className="mt-4 text-[11px] text-slate-500 italic">
                            Powered by PayPal · pay in your local currency at checkout
                        </p>
                    </Card>
                </div>
            )}
        </div>
    );
}

let _rzpLoaded = false;
function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (_rzpLoaded || window.Razorpay) { _rzpLoaded = true; return resolve(); }
        const s = document.createElement('script');
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.onload = () => { _rzpLoaded = true; resolve(); };
        document.body.appendChild(s);
    });
}
